import React, { useState, useEffect } from 'react';
import {
    collection,
    getDocs,
    doc,
    deleteDoc,
    query,
    orderBy,
    updateDoc
} from 'firebase/firestore';
import { db, auth } from './firebase';
import {
    ChatBubbleLeftRightIcon,
    TrashIcon,
    ClockIcon,
    UserCircleIcon,
    EnvelopeIcon,
    CheckCircleIcon,
    MagnifyingGlassIcon,
    ShieldExclamationIcon
} from '@heroicons/react/24/outline';

const AdminSupport = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const isAdmin = auth.currentUser?.email === process.env.REACT_APP_ADMIN_EMAIL;

    useEffect(() => {
        const fetchTickets = async () => {
            if (!isAdmin) return;
            try {
                const q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const ticketList = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setTickets(ticketList);
            } catch (err) {
                console.error("Erro ao buscar chamados:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchTickets();
    }, [isAdmin]);

    const handleUpdateStatus = async (ticketId, currentStatus) => {
        try {
            const ticketRef = doc(db, 'tickets', ticketId);
            const newStatus = currentStatus === 'Pendente' ? 'Respondido' : 'Pendente';
            await updateDoc(ticketRef, { status: newStatus });
            setTickets(tickets.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
        } catch (err) {
            alert("Erro ao atualizar status.");
        }
    };

    const handleDeleteTicket = async (ticketId) => {
        if (!window.confirm("Deseja excluir este chamado permanentemente?")) return;
        try {
            await deleteDoc(doc(db, 'tickets', ticketId));
            setTickets(tickets.filter(t => t.id !== ticketId));
        } catch (err) {
            alert("Erro ao excluir chamado.");
        }
    };

    if (!isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center">
                <ShieldExclamationIcon className="w-20 h-20 text-red-500/50 mb-6" />
                <h2 className="text-3xl font-black text-white italic uppercase mb-2">Acesso Restrito</h2>
            </div>
        );
    }

    const filteredTickets = tickets.filter(t =>
        t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.message?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-8 animate-fade-in pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-white italic uppercase tracking-tighter">Chamados de Suporte</h1>
                    <p className="text-gray-500 font-medium tracking-wide">Gerencie as solicitações dos alunos em tempo real.</p>
                </div>
                <div className="flex items-center gap-4 bg-blue-500/10 border border-blue-500/20 px-6 py-4 rounded-3xl">
                    <ChatBubbleLeftRightIcon className="w-8 h-8 text-blue-500" />
                    <div>
                        <p className="text-[10px] font-black text-blue-500/70 uppercase">Pendentes</p>
                        <p className="text-2xl font-black text-white">{tickets.filter(t => t.status === 'Pendente').length}</p>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <MagnifyingGlassIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                    type="text"
                    placeholder="Pesquisar por aluno, e-mail ou conteúdo da mensagem..."
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all font-medium"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {/* Tickets List */}
            <div className="space-y-4">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="text-gray-500 uppercase font-black text-xs tracking-widest">Carregando canais de suporte...</p>
                    </div>
                ) : filteredTickets.length > 0 ? (
                    filteredTickets.map((ticket) => (
                        <div key={ticket.id} className="bg-white/[0.03] border border-white/5 rounded-[2rem] p-8 hover:bg-white/[0.05] transition-all group overflow-hidden relative">
                            {/* Status Stripe */}
                            <div className={`absolute top-0 left-0 w-1.5 h-full ${ticket.status === 'Pendente' ? 'bg-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.5)]' : 'bg-emerald-500'}`} />

                            <div className="flex flex-col gap-6">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-gray-400">
                                            <UserCircleIcon className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h3 className="font-black text-white italic uppercase tracking-tight flex items-center gap-2">
                                                {ticket.name}
                                                <span className={`text-[8px] px-2 py-0.5 rounded-full border ${ticket.status === 'Pendente'
                                                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-500'
                                                    : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                                                    }`}>
                                                    {ticket.status}
                                                </span>
                                            </h3>
                                            <div className="flex items-center gap-3 text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">
                                                <EnvelopeIcon className="w-3.5 h-3.5" /> {ticket.userEmail}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 text-[10px] text-gray-500 font-black uppercase bg-black/30 px-3 py-1.5 rounded-lg border border-white/5">
                                        <ClockIcon className="w-4 h-4 text-emerald-500" />
                                        {new Date(ticket.createdAt).toLocaleDateString('pt-BR')} às {new Date(ticket.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>

                                <div className="bg-black/20 rounded-2xl p-6 border border-white/5">
                                    <p className="text-[10px] text-blue-500 font-black uppercase tracking-widest mb-2 italic">Assunto: {ticket.subject}</p>
                                    <p className="text-gray-300 text-sm leading-relaxed font-medium whitespace-pre-wrap">
                                        {ticket.message}
                                    </p>
                                </div>

                                <div className="flex items-center justify-end gap-3 pt-2">
                                    <button
                                        onClick={() => handleUpdateStatus(ticket.id, ticket.status)}
                                        className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all flex items-center gap-2 ${ticket.status === 'Pendente'
                                            ? 'bg-emerald-600 text-white hover:bg-emerald-500'
                                            : 'bg-white/5 text-gray-500 border border-white/5 hover:text-white hover:bg-white/10'
                                            }`}
                                    >
                                        <CheckCircleIcon className="w-4 h-4" />
                                        {ticket.status === 'Pendente' ? 'Marcar como Respondido' : 'Reabrir Chamado'}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteTicket(ticket.id)}
                                        className="p-3 bg-red-500/10 text-red-500 border border-red-500/20 rounded-xl hover:bg-red-500 hover:text-white transition-all"
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="py-20 bg-white/5 border border-dashed border-white/10 rounded-[3rem] text-center">
                        <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                        <p className="text-gray-500 font-black uppercase tracking-widest">Nenhum chamado pendente no momento.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSupport;
